import clsx from 'clsx';
import { MouseEvent, useEffect, useState } from 'react';
import { ConfirmDialog } from '../ConfirmDialog';
import { ContextMenuItem, TreeNodeProps } from './types';

const TreeNode = (props: TreeNodeProps) => {
	const {
		node,
		nodes,
		handleSelectNode,
		expandAll,
		contextMenuItems,
		handleGetChildren,
		hasExpandAll,
		onNodeExpand,
		selectedNode,
	} = props;
	const [expanded, setExpanded] = useState(expandAll);
	const [isEditing, setIsEditing] = useState(false);
	const [newName, setNewName] = useState(node.title);
	const [contextItem, setContextItem] = useState<ContextMenuItem | null>(null);
	const [showContextMenu, setShowContextMenu] = useState(false);
	const [contextMenuPosition, setContextMenuPosition] = useState({
		x: 0,
		y: 0,
	});
	const [showConfirmDialog, setShowConfirmDialog] = useState({
		open: false,
		message: '',
	});
	const [isLoading, setIsLoading] = useState(false);

	const hasChildren = node?.subs;

	useEffect(() => {
		setExpanded(expandAll);
	}, [expandAll]);

	useEffect(() => {
		const handleGlobalClose = () => setShowContextMenu(false);
		window.addEventListener('closeAllContextMenus', handleGlobalClose);
		return () => {
			window.removeEventListener('closeAllContextMenus', handleGlobalClose);
		};
	}, []);

	const toggleExpand = () => {
		if (handleGetChildren && hasChildren && !node?.subs?.length && !hasExpandAll) {
			handleGetChildren(node?.id as string);
		}
		setExpanded(!expanded);
		window.dispatchEvent(new Event('closeAllContextMenus'));
	};

	const handleContextMenu = (e: React.MouseEvent) => {
		e.preventDefault();
		window.dispatchEvent(new Event('closeAllContextMenus'));
		setContextMenuPosition({ x: e.clientX, y: e.clientY });
		setShowContextMenu(true);

		document.addEventListener('click', handleClickOutsideContextMenu);
	};

	const handleClickOutsideContextMenu = () => {
		setShowContextMenu(false);
		document.removeEventListener('click', handleClickOutsideContextMenu);
	};

	const handleContextMenuAction = (contextItem: ContextMenuItem) => {
		const actions = {
			rename: () => {
				setIsEditing(true);
			},
			delete: () => {
				setShowConfirmDialog({
					open: true,
					message: 'Bạn có chắc chắn muốn xóa không?',
				});
			},
		};

		if (contextItem?.actionCode && actions[contextItem.actionCode]) {
			actions[contextItem.actionCode]();
			setContextItem(contextItem);
			setShowContextMenu(false);
		}
	};

	const handleOnBlurRename = () => {
		setShowConfirmDialog({
			open: true,
			message: 'Bạn có chắc chắn muốn đổi tên không?',
		});
	};

	const handleCloseConfirmDialog = () => {
		setShowConfirmDialog({ open: false, message: '' });
		setIsEditing(false);
	};

	const handleConfirm = () => {
		const actions = {
			rename: () => {
				node.title = newName;
				contextItem?.action({
					...node,
					newName,
					nodes: nodes,
				});
				setIsEditing(false);
			},
			delete: () => {
				contextItem?.action(node);
			},
		};

		if (contextItem?.actionCode && actions[contextItem.actionCode]) {
			actions[contextItem.actionCode]();
			handleCloseConfirmDialog();
		}
	};

	if (!hasChildren) {
		return (
			<div>
				<button
					type="button"
					className={clsx(
						'd-flex align-items-center justify-content-between',
						'btn w-100 p-0 p-1',
						'tree-top-btn rounded',
						{
							'text-success': node?.status,
							'bg-light-info text-info': selectedNode === node?.nodeId,
						}
					)}
					onClick={() => handleSelectNode(node)}
					onContextMenu={(e) => {
						contextMenuItems && handleContextMenu(e);
					}}
				>
					<div className="d-flex align-items-center flex-1">
						{/* {node?.status ? (
							<i className="bi bi-check-circle-fill text-success me-1"></i>
						) : (
							<i className="bi bi-x-circle-fill text-danger me-1"></i>
						)} */}
						{isEditing ? (
							<input
								type="text"
								value={newName}
								onChange={(e) => setNewName(e.target.value)}
								onBlur={handleOnBlurRename}
								autoFocus
							/>
						) : (
							<span className='text-start'>{node?.title}</span>
						)}
					</div>
					<div>{node?.action}</div>
				</button>

				{showContextMenu && (
					<div
						className="context-menu"
						style={{
							left: `${contextMenuPosition.x}px`,
							top: `${contextMenuPosition.y}px`,
						}}
					>
						{contextMenuItems?.map((item, index) => (
							<button
								type="button"
								key={index}
								className="btn btn-link d-flex align-items-center w-100 text-start p-0"
								onClick={() => handleContextMenuAction(item)}
							>
								{item?.icon}
								{item?.label}
							</button>
						))}
					</div>
				)}

				{showConfirmDialog?.open && (
					<ConfirmDialog
						show={showConfirmDialog?.open}
						message={showConfirmDialog?.message}
						yes="Xác nhận"
						onYesClick={handleConfirm}
						cancel="Hủy"
						onCancelClick={handleCloseConfirmDialog}
					/>
				)}
			</div>
		);
	}

	return (
		<div>
			<div className="d-flex align-items-center py-1">
				<button
					type="button"
					className="btn btn-link p-0 me-1 d-flex align-items-center justify-content-between flex-1"
					onClick={(e: MouseEvent<HTMLButtonElement>) => {
						e.stopPropagation();

						if (onNodeExpand && !expanded) {
							setIsLoading(true);

							onNodeExpand?.(node, () => {
								setIsLoading(false);
								toggleExpand();
							});
							return;
						}

						toggleExpand();
					}}
				>
					<div className="d-flex align-items-center flex-1">
						{expanded && node?.subs?.length ? (
							<span>
								<i className="bi bi-caret-down-fill"></i>
								<i className="fa-regular fa-folder-open"></i>
							</span>
						) : (
							<span>
								<i className="bi bi-caret-right-fill"></i>
								<i className="fa-solid fa-folder"></i>
							</span>
						)}
						<span className='text-start'>{node.title}</span>
						{isLoading && (
							<span className="indicator-progress d-inline-block">
								<span className="spinner-border spinner-border-sm align-middle ms-1 mb-1"></span>
							</span>
						)}
					</div>
					<div>{node?.action}</div>
				</button>
			</div>

			{expanded && node?.subs && (
				<div className="node-child spaces ml-5 pl-20">
					{node?.subs?.map((child, index) => (
						<TreeNode
							key={index}
							nodes={nodes}
							node={child}
							handleSelectNode={handleSelectNode}
							expandAll={expandAll}
							contextMenuItems={contextMenuItems}
							handleGetChildren={handleGetChildren}
							selectedNode={selectedNode}
						/>
					))}
				</div>
			)}
		</div>
	);
};

export default TreeNode;
